import React, { useState, useEffect, useRef } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';
import { offersFeature } from '../../../config/configOffers';
import { ClipLoader } from 'react-spinners';
import { tokenStore } from 'sharetribe-flex-sdk';
import { getResaleHubMagicLink } from '../../../util/api';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  SettingsPopup,
} from '../../../components';

import css from './TopbarDesktop.module.css';

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [isLoadingSellLink, setIsLoadingSellLink] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const heroPages = ['LandingPage', 'SellHomePage'];
  const isHeroPage = heroPages.includes(currentPage);
  const classes = classNames(rootClassName || css.root, className, {
    [css.floatingNav]: isHeroPage,
  });

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 370);
      return () => clearTimeout(timer);
    }
  }, [isSearchOpen]);

  const handleSearchOpen = () => setIsSearchOpen(true);

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchValue('');
  };

  const handleSearchSubmit = e => {
    e.preventDefault();
    if (searchValue.trim()) {
      onSearchSubmit({ keywords: searchValue.trim() });
    }
    handleSearchClose();
  };

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const signupLink = isAuthenticatedOrJustHydrated ? (
    <Menu>
      <MenuLabel>
        <span className={css.titleTextStyle}>
          {intl.formatMessage({ id: 'TopbarDesktop.account' })}
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="InboxPage" params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}>
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.inbox" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="OffersPage">
          {offersFeature && (
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('OffersPage'))}
              name="OffersPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourOffersLink" />
            </NamedLink>
          )}
        </MenuItem>

        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.titleTextStyle}>
        {intl.formatMessage({ id: 'TopbarDesktop.login' })}
      </span>
    </NamedLink>
  );

  const handleSellLinkClick = async e => {
    e.preventDefault();

    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    setIsLoadingSellLink(true);

    try {
      const response = await getResaleHubMagicLink();
      window.open(response.magicLink, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('SellLink error:', err);
    } finally {
      setIsLoadingSellLink(false);
    }
  };

  const resaleHubSellButtonsEnabled =
    process.env.REACT_APP_RESALE_HUB_SELL_BUTTONS_ENABLED === 'true';

  return (
    <nav className={classes}>
      {/* Logo sits outside the card, transparent over the hero */}
      <div className={css.logoArea}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
        />
      </div>

      {/* The card: dark strip on top, white nav below */}
      <div className={css.navCard}>
        {/* Dark utility strip */}
        <div className={css.utilityStrip}>
          <span className={css.utilityStripLabel}>
            <FormattedMessage id="TopbarDesktop.preOwned" />
          </span>
          <div className={css.utilityStripContent}>
            <SettingsPopup currentPage={currentPage} />
          </div>
        </div>

        {/* White main nav row */}
        <div className={css.mainNav}>
          {isSearchOpen ? (
            <form className={css.searchInputRow} onSubmit={handleSearchSubmit}>
              <svg
                className={css.searchIcon}
                width="18"
                height="18"
                viewBox="0 0 21 22"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g
                  transform="matrix(-1 0 0 1 20 1)"
                  strokeWidth="2"
                  fill="none"
                  fillRule="evenodd"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stroke="var(--colorGrey300)"
                >
                  <path d="M13 14l5.241 5.241" />
                  <circle cx="7.5" cy="7.5" r="7.5" />
                </g>
              </svg>
              <input
                ref={searchInputRef}
                className={css.searchInput}
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
                autoComplete="off"
              />
              <button type="button" className={css.searchCloseButton} onClick={handleSearchClose}>
                <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                  <g stroke="var(--colorBlack)" strokeWidth="1.5" fill="none" strokeLinecap="round">
                    <line x1="1" y1="1" x2="13" y2="13" />
                    <line x1="13" y1="1" x2="1" y2="13" />
                  </g>
                </svg>
              </button>
            </form>
          ) : (
            <>
              <div className={css.navLinks}>
                <NamedLink
                  name="SearchPage"
                  className={css.navItem}
                  to={
                    currentPage === 'SearchPage'
                      ? { pathname: '/shop', search: '?refresh=true' }
                      : undefined
                  }
                >
                  <span className={css.titleTextStyle}>
                    {intl.formatMessage({ id: 'TopbarDesktop.shop' })}
                  </span>
                </NamedLink>

                {resaleHubSellButtonsEnabled ? (
                  <a href="#" onClick={handleSellLinkClick} className={css.navItem}>
                    <span className={css.titleTextStyle}>
                      {intl.formatMessage({ id: 'TopbarDesktop.sell' })}
                      {isLoadingSellLink && (
                        <ClipLoader color="currentColor" size={10} className={css.loadingSpinner} />
                      )}
                    </span>
                  </a>
                ) : (
                  <NamedLink name="SellHomePage" className={css.navItem}>
                    <span className={css.titleTextStyle}>
                      {intl.formatMessage({ id: 'TopbarDesktop.sell' })}
                    </span>
                  </NamedLink>
                )}

                <NamedLink name="HowItWorksPage" className={css.navItem}>
                  <span className={css.titleTextStyle}>
                    {intl.formatMessage({ id: 'TopbarDesktop.howItWorks' })}
                  </span>
                </NamedLink>

                <a href="https://www.marshall.com/gb/en" className={css.navItem}>
                  <span className={css.titleTextStyle}>
                    {intl.formatMessage({ id: 'TopbarDesktop.brandLink' })}
                  </span>
                </a>
              </div>

              <div className={classNames(css.rightItemsContainer, css.nonSelectable)}>
                <button className={css.topbarTextButton} onClick={handleSearchOpen}>
                  <span className={css.titleTextStyle}>
                    {intl.formatMessage({ id: 'TopbarDesktop.search' })}
                  </span>
                </button>
                {signupLink}
              </div>
            </>
          )}
        </div>
      </div>

      {isSearchOpen && <div className={css.searchOverlay} onClick={handleSearchClose} />}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
